import { createSelector } from 'reselect';
import _get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import forEach from 'lodash/forEach';
import find from 'lodash/find';
import {
    getBundleQuantity,
    makeInitProductOption,
    getInventory,
} from './BundleProductOptionsSelector';
import ProductConstants from '../../helpers/ProductConstants';

const getBundleItemOffering = (state) => state.bundleItemOffering;

/* istanbul ignore next */
const getBundleInventoryStatus = createSelector(
    [getBundleItemOffering, getBundleQuantity],
    (bundleItemOffering = {}, bundleQuantity = {}) => {
        const defaultValue = null;
        let inventoryStatus = {};
        const inventoryData = Object.keys(bundleItemOffering);
        if (inventoryData.length) {
            inventoryData.forEach((ppid) => {
                let shipToHomeMOQ;
                let isShipToHomeAvailable;
                let isInventoryAvailable = true;
                let inventoryErrorMessage = {};
                if (_get(bundleItemOffering, [ppid, 'promise'])) {
                    shipToHomeMOQ = _get(
                        bundleItemOffering[ppid].promise,
                        'skuDeliveryOptions[0].deliveryOptions.shipToHome.availableQuantity',
                        defaultValue
                    );
                    const shipToHomeAvailabilityStatus = _get(
                        bundleItemOffering[ppid],
                        'promise.skuDeliveryOptions[0].deliveryOptions.shipToHome.availabilityStatus',
                        false
                    );
                    isShipToHomeAvailable =
                        shipToHomeAvailabilityStatus && shipToHomeAvailabilityStatus !== 'NA';
                } else {
                    shipToHomeMOQ = _get(
                        bundleItemOffering[ppid],
                        'inventory.maxOrderQuantity',
                        defaultValue
                    );
                    isShipToHomeAvailable = _get(bundleItemOffering[ppid], 'inventory.atp', false);
                }
                if (!isShipToHomeAvailable) {
                    isInventoryAvailable = false;
                } else if (!isEmpty(shipToHomeMOQ) && shipToHomeMOQ < bundleQuantity[ppid]) {
                    isInventoryAvailable = false;
                } else {
                    isInventoryAvailable = true;
                }
                if (!isInventoryAvailable) {
                    inventoryErrorMessage = {
                        section: `INVENTORY_UNAVAILABLE_MESSAGES_SECTION_${ppid}`,
                        message: {
                            id: `INVENTORY_UNAVAILABLE_${ppid}`,
                            title: ProductConstants.INVENTORY_UNAVAILABLE,
                        },
                    };
                }

                inventoryStatus = {
                    ...inventoryStatus,
                    [ppid]: {
                        isInventoryAvailable,
                        inventoryErrorMessage,
                        itemQuantitySelected: bundleQuantity[ppid] > 0,
                    },
                };
            });
        }
        const inventoryAvailableForQuantitySelectedItems = [];
        Object.keys(inventoryStatus).forEach((key) => {
            if (
                inventoryStatus[key].isInventoryAvailable &&
                inventoryStatus[key].itemQuantitySelected
            ) {
                inventoryAvailableForQuantitySelectedItems.push(true);
            }
        });
        const isEveryInventoryAvailable = inventoryAvailableForQuantitySelectedItems.every(Boolean);

        return {
            inventoryStatus,
            isEveryInventoryAvailable,
        };
    }
);

export const getProductOptions = (state) => {
    const getInitProductOption = makeInitProductOption();
    let selectedProducts = {};
    forEach(state.productDetails.products, (products, index) => {
        selectedProducts = getInitProductOption(state, index);
    });
    return selectedProducts;
};

export const getInventoryForQuantityDropdown = (bundleChildPPiD) =>
    createSelector(
        [getBundleInventoryStatus, getBundleItemOffering, getProductOptions, getInventory],
        (
            bundleInventoryStatus = {},
            bundleItemOffering = {},
            bundleProductOptions,
            bundleInventory
        ) => {
            const itemOfferings = _get(bundleItemOffering, `${bundleChildPPiD}`, {});
            const promiseSKUId = _get(itemOfferings, 'promise.skuDeliveryOptions[0].skuId', '');
            const productOptions = _get(bundleProductOptions, `${bundleChildPPiD}`, {});
            const filteredItems = _get(productOptions, 'filteredItems', []);
            const optionsSelected = _get(productOptions, 'optionsSelected', []);
            const unavailableSKUCombination = isEmpty(filteredItems) && !isEmpty(optionsSelected);
            const filteredSKU = _get(filteredItems, '[0].id', '');
            const offeringAvailable = _get(productOptions, 'pricingURL.itemOfferingCall', false);
            let isItemInStock = false;
            if (unavailableSKUCombination) {
                isItemInStock = false;
            } else if (promiseSKUId === filteredSKU && offeringAvailable) {
                isItemInStock = _get(
                    bundleInventoryStatus,
                    `inventoryStatus.${bundleChildPPiD}.isInventoryAvailable`,
                    false
                );
            } else if (!isEmpty(filteredSKU) && !offeringAvailable) {
                const inventoryObj = _get(bundleInventory, '[0].inventory', []);
                const skuInventory = find(inventoryObj, {
                    id: filteredSKU,
                });
                isItemInStock = skuInventory?.atp || false;
            } else {
                isItemInStock = productOptions.isProductInStock || productOptions.isItemInStock;
            }
            return isItemInStock;
        }
    );

export default getBundleInventoryStatus;
